import { useQuery } from '@apollo/client/react';
import {
  DimeAvatar,
  DimeAvatarFallback,
  DimeButton,
  DimeChip,
  DimeSpinner,
} from '@nudool/design-system';
import { createFileRoute, useNavigate } from '@tanstack/react-router';

import { useBreadcrumbLabel } from '../../components/BreadcrumbLabelsContext.js';
import { useDetailViewState } from '../../hooks/use-detail-view-state.js';
import { useDeniedTooltip } from '../../lib/access/index.js';
import { assertUuidRouteParams } from '../../lib/assert-uuid-route-params.js';
import {
  GET_DEPARTMENT,
  type GetDepartmentQueryResult,
  type GetDepartmentQueryVariables,
} from '../../modules/departments/operations.js';
import {
  GET_SYS_USER,
  type GetSysUserQueryResult,
  type GetSysUserQueryVariables,
} from '../../modules/iam/operations/index.js';
import { usePermissions } from '../../modules/iam/permissions-context.js';
import { useAdminAccountLock } from '../../modules/users/use-admin-account-lock.js';
import { UserActiveStatusControl } from '../../modules/users/user-active-status-control.js';
import { UserDetailAccessTab } from '../../modules/users/user-detail-access-tab.js';
import { UserEditDialog } from '../../modules/users/user-edit-dialog.js';

export const Route = createFileRoute('/users/$userId')({
  beforeLoad: ({ params }) => assertUuidRouteParams(params),
  validateSearch: (search: Record<string, unknown>) => ({
    edit: search.edit === true || search.edit === 'true',
  }),
  component: UserDetailPage,
});

// eslint-disable-next-line react-refresh/only-export-components
function UserDetailPage() {
  const { userId } = Route.useParams();
  const { edit: startEditing } = Route.useSearch();
  const { permissions } = usePermissions();
  const canEdit = permissions.includes('user:edit');
  const deniedTooltip = useDeniedTooltip();
  const adminAccountLock = useAdminAccountLock(userId);
  const navigate = useNavigate();
  const { isEditing, enterEditMode, exitEditMode } = useDetailViewState({
    recordId: userId,
    initialEditing: startEditing,
    defaultTab: 'access',
    onEditingChange: (editing) =>
      void navigate({
        to: '/users/$userId',
        params: { userId },
        search: { edit: editing },
        replace: true,
      }),
  });

  const { data, loading, refetch } = useQuery<GetSysUserQueryResult, GetSysUserQueryVariables>(
    GET_SYS_USER,
    { variables: { id: userId } },
  );

  const user = data?.sysUser ?? null;
  // Looked up by id rather than the active-only list, so a since-deactivated department still shows its name.
  const { data: departmentData } = useQuery<GetDepartmentQueryResult, GetDepartmentQueryVariables>(
    GET_DEPARTMENT,
    { variables: { id: user?.departmentId ?? '' }, skip: !user?.departmentId },
  );
  const departmentName = departmentData?.department?.name ?? null;
  const fullName =
    [user?.firstName?.trim(), user?.lastName?.trim()].filter(Boolean).join(' ') || undefined;

  useBreadcrumbLabel(userId, fullName);

  // Only the first load blanks the page; a refetch keeps it mounted so an unsaved access draft survives.
  if (loading && !data) {
    return (
      <div className="text-muted-foreground flex items-center gap-2 text-sm">
        <DimeSpinner />
        Loading…
      </div>
    );
  }
  if (!user) return <div className="text-muted-foreground text-sm">User not found.</div>;

  const initials = [user.firstName?.[0], user.lastName?.[0]].filter(Boolean).join('').toUpperCase();
  const displayName = fullName ?? '—';

  return (
    <div className="flex flex-1 flex-col gap-4">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
        <div className="flex min-w-0 flex-1 basis-64 items-center gap-4">
          <DimeAvatar className="size-12 shrink-0 rounded-xl after:rounded-xl">
            <DimeAvatarFallback className="rounded-xl text-lg font-semibold tracking-tight">
              {initials || '?'}
            </DimeAvatarFallback>
          </DimeAvatar>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="font-heading text-xl leading-tight font-semibold tracking-tight">
                {displayName}
              </h1>
              <DimeChip variant={user.activeFlag ? 'success' : 'destructive'}>
                {user.activeFlag ? 'Active' : 'Inactive'}
              </DimeChip>
            </div>
            <p className="text-muted-foreground truncate text-[13px]">
              <span>{user.email ?? '—'}</span>
              {[departmentName, user.phone?.trim()].filter(Boolean).map((detail) => (
                <span key={detail}>
                  <span aria-hidden> · </span>
                  {detail}
                </span>
              ))}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <DimeButton
            type="button"
            variant="outline"
            onClick={enterEditMode}
            disabled={!canEdit || adminAccountLock !== null}
            disabledTooltip={adminAccountLock ?? deniedTooltip('user:edit')}
          >
            Edit details
          </DimeButton>
          <UserActiveStatusControl
            userId={user.id!}
            userName={displayName}
            activeFlag={user.activeFlag}
            onStatusChanged={() => void refetch()}
            disabled={!canEdit || adminAccountLock !== null}
            disabledTooltip={adminAccountLock ?? deniedTooltip('user:edit')}
          />
        </div>
      </div>

      <UserDetailAccessTab user={user} onChanged={() => void refetch()} />

      {isEditing && (
        <UserEditDialog
          user={user}
          onCancel={exitEditMode}
          onSaved={() => {
            exitEditMode();
            void refetch();
          }}
        />
      )}
    </div>
  );
}
